/**
 * Lightweight page snapshot — turns a DOM element into a PNG or PDF the
 * user can download. Used by the "Export" menu in `PageHeader.actions`.
 *
 * We use `html-to-image` instead of full headless Chrome (no server hop)
 * so this works completely offline. The output isn't pixel-perfect with
 * very heavy charts, but it's well within "looks great in a deck".
 */
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';

export type SnapshotFormat = 'png' | 'pdf';

interface SnapshotOptions {
  /** Element to capture. Default: the closest `<main>` element. */
  el?: HTMLElement | null;
  /** Filename root, e.g. `overview-2026-04-22`. Extension auto-appended. */
  filename: string;
  format: SnapshotFormat;
  /** Background colour to flash behind transparent content (default: surface). */
  background?: string;
  /** Image quality 0..1 for PNG (default: 0.95). */
  quality?: number;
}

function findCaptureEl(): HTMLElement | null {
  return (
    (document.querySelector('main') as HTMLElement | null) ??
    (document.getElementById('root') as HTMLElement | null) ??
    document.body
  );
}

function getCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `hsl(${v})` : fallback;
}

export async function snapshotPage({
  el,
  filename,
  format,
  background,
  quality = 0.95,
}: SnapshotOptions): Promise<void> {
  const target = el ?? findCaptureEl();
  if (!target) throw new Error('No element to snapshot.');

  const bg = background ?? getCssVar('--surface', '#ffffff');

  const dataUrl = await toPng(target, {
    pixelRatio: Math.min(2, window.devicePixelRatio || 1),
    backgroundColor: bg,
    cacheBust: true,
    quality,
    style: {
      // Hide skeleton placeholders so the export shows real numbers.
      animation: 'none',
    },
    filter: (node) => {
      if (!(node instanceof HTMLElement)) return true;
      // Allow callers to opt-out specific elements with `data-snapshot-skip`.
      return node.dataset.snapshotSkip !== '1';
    },
  });

  if (format === 'png') {
    triggerDownload(dataUrl, `${filename}.png`);
    return;
  }

  // PDF: paginate by image height vs A4 landscape.
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  const pdf = new jsPDF({
    orientation: img.width >= img.height ? 'landscape' : 'portrait',
    unit: 'pt',
    format: 'a4',
  });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const ratio = Math.min(pageW / img.width, pageH / img.height);
  const renderW = img.width * ratio;
  const renderH = img.height * ratio;
  const x = (pageW - renderW) / 2;
  const y = (pageH - renderH) / 2;
  pdf.addImage(dataUrl, 'PNG', x, y, renderW, renderH, undefined, 'FAST');
  pdf.save(`${filename}.pdf`);
}

function triggerDownload(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
